import { colors, fontFamily, fontSize } from "../../assets/styles/parameters";

import ReactSelect from "react-select";
import styled from "styled-components";

// ==========================================
// --- LAYOUT WRAPPERS ---
// ==========================================

export const FormContainer = styled.form`
  width: 100%;
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  width: 100%;
`;

export const Label = ({ text, entity }) => {
  return (
    <BaseLabel>
      {text} {entity && <RequiredStar>{entity}</RequiredStar>}
    </BaseLabel>
  );
};

// ==========================================
// --- STANDARD TEXT INPUT ---
// ==========================================

export const Input = ({ input, meta, label, entity, ...otherProps }) => {
  return (
    <InputContainer>
      {label && <Label text={label} entity={entity} />}

      <BaseInput {...input} {...otherProps} />

      {meta && meta.touched && meta.error && (
        <ErrorSpan>{meta.error}</ErrorSpan>
      )}
    </InputContainer>
  );
};

// ==========================================
// --- TEXT AREA ---
// ==========================================

// Re-added the missing CustomTextArea mapped to our new styling
export const CustomTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 5px;
  border: 0.5px solid ${colors.gray400};
  background-color: ${colors.gray200};
  font-family: ${fontFamily.body}, Arial, sans-serif;
  font-size: ${fontSize.default};
  transition: all 0.2s ease-in-out;
  color: ${colors.black};
  outline: none;
  min-height: 120px;
  resize: vertical;

  &:focus {
    background-color: ${colors.white};
    border-color: ${colors.black};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

// ==========================================
// --- SELECT INPUTS (React-Select) ---
// ==========================================

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    border: state.isFocused
      ? `1px solid ${colors.black}`
      : `0.5px solid ${colors.gray400}`,
    boxShadow: "none",
    backgroundColor: state.isFocused ? colors.white : colors.gray200,
    borderRadius: "5px",
    minHeight: "45px",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
  }),
  container: (base) => ({ ...base, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    marginTop: "4px",
    zIndex: 1000,
    borderRadius: "5px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  }),
  menuList: (base) => ({ ...base, padding: 0 }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    color: colors.black,
    backgroundColor: isSelected
      ? colors.gray300
      : isFocused
        ? colors.gray100
        : colors.white,
    padding: "10px 12px",
    cursor: "pointer",
    "&:active": { backgroundColor: colors.gray300 },
  }),
};

export const Select = ({
  name,
  options = [],
  value,
  onChange,
  className,
  placeholder,
  noOptionsMessage,
  ...otherProps
}) => {
  return (
    <ReactSelect
      {...otherProps}
      className={className}
      onChange={onChange}
      value={value}
      placeholder={placeholder}
      name={name}
      options={options}
      isSearchable={false}
      maxMenuHeight={200}
      noOptionsMessage={noOptionsMessage}
      styles={customSelectStyles}
    />
  );
};

// Re-added to satisfy your index.js export routing
export const CustomSelect = Select;

export const SelectFinalForm = ({
  input,
  meta,
  label,
  options,
  entity,
  ...otherProps
}) => {
  const selectedOption =
    options?.find((opt) => opt.value === input.value) || null;

  return (
    <InputContainer>
      {label && <Label text={label} entity={entity} />}

      <ReactSelect
        {...otherProps}
        name={input.name}
        options={options}
        value={selectedOption}
        onChange={(option) => input.onChange(option ? option.value : "")}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        isSearchable={false}
        maxMenuHeight={200}
        styles={customSelectStyles}
      />

      {meta && meta.touched && meta.error && (
        <ErrorSpan>{meta.error}</ErrorSpan>
      )}
    </InputContainer>
  );
};

// ==========================================
// --- STYLED COMPONENTS ---
// ==========================================

export const BaseLabel = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.black};
`;

const RequiredStar = styled.span`
  color: #dc3545;
  margin-left: 2px;
`;

export const BaseInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 5px;
  border: 0.5px solid ${colors.gray400};
  background-color: ${colors.gray200};
  font-family: ${fontFamily.body}, Arial, sans-serif;
  font-size: ${fontSize.default};
  transition: all 0.2s ease-in-out;
  color: ${colors.black};
  outline: none;

  &:focus {
    background-color: ${colors.white};
    border-color: ${colors.black};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorSpan = styled.span`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-weight: 500;
  display: block;
`;
